// ─────────────────────────────────────────────────────────────────────────
// LoginChooser.js
//
// Tiny wrapper that picks which login skin to show based on the
// facility setting `login_style_split` (in `facility_settings`, managed
// from Admin → Hospital Admin Settings → Module Toggles).
//
//   • setting absent or 'true' → LoginSplit ("Welcome Back" split-pane
//     layout with rotating image carousel) — the standard login.
//   • setting 'false'          → classic Login (kept for any facility
//     that explicitly switches back).
//
// First-paint behaviour:
//   • If there's a cached choice in localStorage, render that skin
//     immediately (no flash, no fetch wait).
//   • If there's no cached choice yet (e.g. first ever visit on this
//     browser), render a neutral black holding screen instead of the
//     classic login — otherwise admins who chose `split` would see
//     classic flash for ~200ms while the fetch resolves.
//   • Once /facilitySettings comes back, persist the answer to
//     localStorage so every subsequent visit is instant.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Login from '../Login';
import LoginSplit from './LoginSplit';
import { facilitySettingsAPI } from '../BaseURL';

const STYLE_KEY      = 'login_style_split';
const CACHE_KEY      = 'login_style';

// Returns 'split' | 'classic' | null (null = no cached preference yet).
// Keeping null distinct lets the chooser show a neutral holding screen
// on the very first visit instead of picking a side prematurely.
function readCachedStyle() {
  try {
    const v = localStorage.getItem(CACHE_KEY);
    if (v === 'split')   return 'split';
    if (v === 'classic') return 'classic';
  } catch (_) {}
  return null;
}

function writeCachedStyle(style) {
  try { localStorage.setItem(CACHE_KEY, style); } catch (_) {}
}

// Normalises whatever the settings endpoint returns into our two-value
// enum. Split is the default — only an explicit "off" value picks classic.
function pickStyle(value) {
  if (value === false || value === 0) return 'classic';
  const s = String(value ?? '').trim().toLowerCase();
  if (s === 'false' || s === '0' || s === 'no' || s === 'off' || s === 'classic') return 'classic';
  return 'split';
}

export default function LoginChooser() {
  const [style, setStyle] = useState(readCachedStyle);   // 'split' | 'classic' | null

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(facilitySettingsAPI, { timeout: 8000 });
        if (!alive) return;
        const row = data?.success && Array.isArray(data.results)
          ? data.results.find((r) => r.key === STYLE_KEY)
          : null;
        const next = pickStyle(row?.value);
        setStyle((prev) => (prev === next ? prev : next));
        writeCachedStyle(next);
      } catch (_) {
        // Endpoint unreachable → fall back to whatever's cached, or to
        // split if nothing's cached yet. Either way the user can sign
        // in; we just can't honour the admin's preference this load.
        if (!alive) return;
        setStyle((prev) => prev || 'split');
      }
    })();
    return () => { alive = false; };
  }, []);

  // First-paint with no cache: show a neutral black holding screen so
  // the wrong login skin never flashes. The screen is the same pure
  // black the split login uses, so when it resolves to split the
  // transition is invisible; if it resolves to classic the swap is
  // brief and predictable.
  if (style === null) {
    return (
      <Box sx={{
        minHeight: '100vh',
        bgcolor: '#000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CircularProgress size={28} sx={{ color: 'rgba(255,255,255,0.35)' }} />
      </Box>
    );
  }

  return style === 'split' ? <LoginSplit /> : <Login />;
}
